/**
 * Site Group Settings V2 — "Curfew" section: the recurring window
 * that blocks non-privileged power commands and can trim active
 * schedules to end within it.
 *
 * Contains:
 * - CurfewSection (default export)
 */

import NightsStayIcon from '@mui/icons-material/NightsStay';
import { Box, Switch } from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line no-restricted-imports -- genuine warning, not a destructive confirm
import WarningDialog from '@sophocles/components/WarningDialog';
import {
  SettingsFieldRow,
  SettingsSection,
  SettingsSubsection,
  SettingsSwitchField,
  SettingsTimePickerField,
} from '@sophocles/views/settings/components';

// Curfew section.  Mirrors the legacy `SiteGroupCurfewTab` 1:1 —
// enable / start / end / trim_schedules.  The consolidated PATCH
// preserves the legacy side effects (TIME_SET command broadcast,
// optional schedule-trim async task) by delegating server-side to
// the existing `SiteGroupCurfewSerializer.update`.
//
// `trim_schedules` is a write-only flag (the server pops it out of
// validated_data before saving) and the legacy UI gates the toggle
// behind a "are you sure" warning because trimming schedules can't
// be undone.  Preserve that confirmation 1:1 here using a Formik
// helper to flip the field only after the user confirms.
export default function CurfewSection({ disabled, isBillboard }) {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const enabled = Boolean(values.curfew?.enabled);
  const trimSchedules = Boolean(values.curfew?.trim_schedules);
  const groupTerm = t('siteGroups.siteGroup');
  const [warnTrim, setWarnTrim] = React.useState(false);

  return (
    <SettingsSection
      id="settings-curfew"
      icon={<NightsStayIcon />}
      title={t('siteGroupSettings.v2.curfew', 'Curfew')}
      description={t(
        'siteGroupSettings.v2.curfewDescription',
        'Block non-privileged power commands and require schedules to end during a recurring time window.',
      )}
    >
      <SettingsSubsection title={t('siteGroupSettings.v2.curfewWindow', 'Window')} bordered>
        <SettingsFieldRow
          label={t('siteGroupSettings.v2.curfewEnabled', 'Curfew Enabled')}
          description={t(
            'siteGroupSettings.v2.curfewEnabledDescription',
            'When on, schedules must end and non-privileged users cannot issue power commands during the window below.',
          )}
          controlWidth={80}
        >
          <SettingsSwitchField name="curfew.enabled" hideLabel />
        </SettingsFieldRow>
        {enabled ? (
          <>
            <SettingsFieldRow
              label={t('siteGroupSettings.startTime', 'Start Time')}
              description={t(
                'siteGroupSettings.v2.curfewStartDescription',
                'Site-local time when curfew restrictions begin.',
              )}
              controlWidth={180}
            >
              <SettingsTimePickerField name="curfew.start_time" views={['hours', 'minutes']} hiddenLabel />
            </SettingsFieldRow>
            <SettingsFieldRow
              label={t('siteGroupSettings.endTime', 'End Time')}
              description={t(
                'siteGroupSettings.v2.curfewEndDescription',
                'Site-local time when curfew restrictions release. Wraps past midnight if earlier than start.',
              )}
              controlWidth={180}
            >
              <SettingsTimePickerField name="curfew.end_time" views={['hours', 'minutes']} hiddenLabel />
            </SettingsFieldRow>
          </>
        ) : null}
      </SettingsSubsection>

      <SettingsSubsection title={t('siteGroupSettings.v2.curfewActions', 'Actions')} bordered>
        <SettingsFieldRow
          label={t('siteGroupSettings.trimActiveSchedules', 'Trim Active Schedules')}
          description={t(
            'siteGroupSettings.v2.curfewTrimDescription',
            'On save, shorten any active schedules so they end inside the curfew window. Cannot be undone.',
          )}
          controlWidth={80}
        >
          {/*
            Raw Switch (not SettingsSwitchField) — we need a custom
            onChange to pop the warning dialog before flipping on.
            Off→on requires explicit confirmation; on→off is a
            normal toggle.
          */}
          <Switch
            checked={trimSchedules}
            disabled={disabled || !enabled}
            onChange={(_, checked) => {
              if (checked) {
                setWarnTrim(true);
              } else {
                setFieldValue('curfew.trim_schedules', false);
              }
            }}
          />
        </SettingsFieldRow>
      </SettingsSubsection>

      <WarningDialog
        title={t('general.areYouSure', 'Are you sure?')}
        open={warnTrim}
        onConfirm={() => {
          setFieldValue('curfew.trim_schedules', true);
          setWarnTrim(false);
        }}
        onCancel={() => setWarnTrim(false)}
        body={
          <Box>
            <Box sx={{ mb: 1 }}>{t('siteGroupSettings.trimWarningMessage', { term: groupTerm })}</Box>
            <Box>{t('siteGroupSettings.trimWarningCannotUndo', 'This action cannot be undone.')}</Box>
          </Box>
        }
      />
    </SettingsSection>
  );
}
